'use client';

import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { startWhisperAi, getCookieValue } from '@nx-frontend/shared/actions';
import { axiosClient } from '@nx-frontend/shared/api';
import { getIsAddonWasPurshasedEarly } from '@nx-frontend/shared/api/webnotes/get-is-addon-was-purchased-early';
import { generalEnv } from '@nx-frontend/shared/env';
import { useBuyGenAiAddon } from '@nx-frontend/shared/hooks';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import {
  $aiSessions,
  $aiChat,
  updateAIChatEvent,
  $transcriptPanel,
  loadAISessionsEvent,
  updateMessageInSessionEvent,
  addMessageInSessionEvent,
  popMessageInSessionEvent,
} from '@nx-frontend/shared/store/transcript-panel';
import { $user } from '@nx-frontend/shared/store/user';
import { AiUsage } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps, GetOpenUnlockAIPopupProps } from '@nx-frontend/shared/types';
import type { GetMessageTask } from '@nx-frontend/shared/utils';
import { getOpenAddonPopup } from '@nx-frontend/widgets/popups';
import { TranscriptAiInput } from './transcript-ai-input';
import { TranscriptAiSession } from './transcript-ai-session';
import { TranscriptAiWelcome } from './transcript-ai-welcome';

interface TranscriptAiProps {
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
  getOpenUnlockAIPopup: (props: GetOpenUnlockAIPopupProps) => void;
}

export function TranscriptAi({ getOpenPaywallPopup, getOpenUnlockAIPopup }: TranscriptAiProps) {
  const [task, setTask] = useState<GetMessageTask | null>(null);
  const [cancelId, setCancelId] = useState<string>('');
  const [
    aiSessions,
    aiChat,
    updateAIChat,
    currentWorkspace,
    transcriptPanel,
    openModal,
    user,
    orgInfo,
    billingInfo,
    currentOrganization,
    loadAISessions,
    updateMessageInSession,
    addMessageInSession,
    popMessageInSession,
  ] = useUnit([
    $aiSessions,
    $aiChat,
    updateAIChatEvent,
    $currentWorkspace,
    $transcriptPanel,
    openModalEvent,
    $user,
    $orgInfo,
    $billingInfo,
    $currentOrganization,
    loadAISessionsEvent,
    updateMessageInSessionEvent,
    addMessageInSessionEvent,
    popMessageInSessionEvent,
  ]);
  const attachmentGlobalId = transcriptPanel.attachment.globalId;
  const aiSessionId = aiSessions[0].globalId;
  const { messages } = useMemo(() => {
    try {
      return {
        messages: aiSessions[0].messages,
        sessionId: aiSessions[0].globalId,
      };
    } catch (err) {
      return {
        messages: [],
        sessionId: '',
      };
    }
  }, [aiSessions]);

  const openGenAiAddonPopup = useBuyGenAiAddon({
    getOpenPaywallPopup,
    getOpenAddonPopup,
    getIsAddonWasPurshasedEarly,
  });

  useEffect(() => {
    const processPrompt = async () => {
      if ('progress' === aiChat.state && aiChat.canceled) {
        fetch(`${generalEnv.aiServiceUrl}/whisper/cancel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'everhelper-session-id': getCookieValue('eversessionid'),
          },
          body: JSON.stringify({
            cancelId,
          }),
        });
        updateAIChat({
          canceled: false,
        });
      }
      if ('done' === aiChat.state && ('' !== aiChat.prompt || '' !== aiChat.cmd)) {
        const { prompt, cmd } = aiChat;
        updateAIChat({
          prompt: '',
          cmd: '',
        });
        const usage = (
          await axiosClient.post<AiUsage>(`/v2/api/ai/usage`, {
            orgId: currentWorkspace.orgId,
          })
        ).data;
        const cancelIdTmp = generateUuid();
        setCancelId(cancelIdTmp);
        const taskTmp = startWhisperAi({
          usage,
          sessionId: aiSessions[0].globalId,
          orgId: currentWorkspace.orgId,
          workspaceId: currentWorkspace.globalId,
          attachmentId: attachmentGlobalId,
          prompt,
          cmd,
          aiServiceUrl: generalEnv.aiServiceUrl,
          popupParams: {
            openModal,
            subscriptionType: orgInfo.subscriptionType,
            billingInfo,
            currentOrganization,
            user,
            getOpenPaywallPopup,
            openGenAiAddonPopup,
          },
          cancelId: cancelIdTmp,
          onMessage: (v: string) => {
            updateMessageInSession({
              globalId: aiSessionId,
              fragment: v,
            });
          },
        });
        setTask(taskTmp);
        updateAIChat({
          state: 'progress',
        });
        addMessageInSession({
          globalId: aiSessionId,
          role: 'user',
          content: prompt || cmd,
        });
        addMessageInSession({
          globalId: aiSessionId,
          role: 'assistant',
          content: '',
        });
        try {
          await taskTmp.run();
        } catch (err) {
          //usage limit
          popMessageInSession({
            globalId: aiSessionId,
          });
          popMessageInSession({
            globalId: aiSessionId,
          });
        }
        updateAIChat({
          prompt: '',
          cmd: '',
          state: 'done',
        });
      }
    };
    processPrompt();
  }, [
    addMessageInSession,
    aiChat,
    aiSessions,
    attachmentGlobalId,
    billingInfo,
    currentOrganization,
    currentWorkspace.globalId,
    currentWorkspace.orgId,
    loadAISessions,
    openModal,
    orgInfo.subscriptionType,
    updateAIChat,
    updateMessageInSession,
    user,
    aiSessionId,
    task,
    setTask,
    cancelId,
    setCancelId,
    popMessageInSession,
    getOpenPaywallPopup,
    getOpenUnlockAIPopup,
    openGenAiAddonPopup,
  ]);

  return (
    <>
      {messages.length ? <TranscriptAiSession /> : <TranscriptAiWelcome />}
      <TranscriptAiInput />
    </>
  );
}
